import { useCallback, useEffect, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { Button } from '@silverfalls/sterling';
import { useToast } from '../components/Toast.js';
import { formatDateTime } from '../lib/format.js';
import {
  fetchOpcoPaymentConfig,
  setOpcoPaymentConfig,
  type DiscountPolicy,
  type OpcoPaymentConfig,
  type PaymentRunPaymentMethod,
} from '../api/p2p.js';

const REQUESTING_USER = 'u-ui-skeleton';

const DISCOUNT_POLICIES: ReadonlyArray<{ value: DiscountPolicy; label: string }> = [
  { value: 'TAKE_WHEN_ECONOMIC', label: 'Take when economic' },
  { value: 'ALWAYS_TAKE', label: 'Always take' },
  { value: 'ALWAYS_SKIP', label: 'Always skip' },
];

const PAYMENT_METHODS: ReadonlyArray<PaymentRunPaymentMethod> = [
  'ACH',
  'CHECK',
  'WIRE',
  'VIRTUAL_CARD',
];

interface PaymentConfigSettingsProps {
  readonly opcoRef: string;
}

export function PaymentConfigSettings({
  opcoRef,
}: PaymentConfigSettingsProps): JSX.Element {
  const toast = useToast();
  const [config, setConfig] = useState<OpcoPaymentConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [discountPolicy, setDiscountPolicy] = useState<DiscountPolicy>(
    'TAKE_WHEN_ECONOMIC',
  );
  const [threshold, setThreshold] = useState('25000');
  const [defaultPaymentMethod, setDefaultPaymentMethod] =
    useState<PaymentRunPaymentMethod>('ACH');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const cfg = await fetchOpcoPaymentConfig(opcoRef);
      setConfig(cfg);
      if (cfg) {
        setDiscountPolicy(cfg.discountPolicy);
        setThreshold(cfg.dualApprovalThresholdAmount);
        setDefaultPaymentMethod(cfg.defaultPaymentMethod);
      }
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [opcoRef]);

  useEffect(() => {
    void load();
  }, [load]);

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setSubmitting(true);
      setSubmitError(null);
      try {
        const result = await setOpcoPaymentConfig({
          opcoRef,
          discountPolicy,
          dualApprovalThresholdAmount: threshold.trim(),
          defaultPaymentMethod,
          requestingUser: REQUESTING_USER,
        });
        if (result.__typename === 'SetOpcoPaymentConfigFailure') {
          const msg = `${result.class}: ${JSON.stringify(result.details)}`;
          setSubmitError(msg);
          toast.push(msg, 'error');
          setSubmitting(false);
          return;
        }
        if (result.changed) {
          toast.push('Payment config saved.', 'success');
        } else {
          toast.push('No changes — config matched current values.', 'info');
        }
        await load();
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setSubmitError(msg);
        toast.push(msg, 'error');
      } finally {
        setSubmitting(false);
      }
    },
    [opcoRef, discountPolicy, threshold, defaultPaymentMethod, toast, load],
  );

  return (
    <section className="vendor-detail">
      <div className="screen-header">
        <div className="entity-header">
          <h2>Payment config — {opcoRef}</h2>
          <div className="entity-summary">
            <span className="entity-summary-part">
              Controls how SchedulePaymentRun behaves for this OPCO
            </span>
            {config && (
              <span className="entity-summary-part mono">
                last updated {formatDateTime(config.updatedAt)} by {config.updatedBy}
              </span>
            )}
          </div>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-neg" role="alert">
          Couldn't load config: {loadError}
        </div>
      )}

      {loading ? (
        <div className="t-body-sm">Loading…</div>
      ) : (
        <div className="detail-grid">
          <div className="detail-card">
            <h3 className="detail-card-title">Settings</h3>
            <form className="form-grid" onSubmit={onSubmit}>
              <label className="form-row">
                <span className="form-label">Discount policy</span>
                <select
                  value={discountPolicy}
                  onChange={(e) => setDiscountPolicy(e.target.value as DiscountPolicy)}
                  required
                >
                  {DISCOUNT_POLICIES.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="form-row">
                <span className="form-label">Dual-approval threshold</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={threshold}
                  onChange={(e) => setThreshold(e.target.value)}
                  required
                  placeholder="USD; payment runs at/above this need a 2nd approver"
                />
              </label>
              <label className="form-row">
                <span className="form-label">Default payment method</span>
                <select
                  value={defaultPaymentMethod}
                  onChange={(e) =>
                    setDefaultPaymentMethod(e.target.value as PaymentRunPaymentMethod)
                  }
                  required
                >
                  {PAYMENT_METHODS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </label>
              {submitError && (
                <div className="alert alert-neg" role="alert">
                  {submitError}
                </div>
              )}
              <div className="form-row form-row-full">
                <span className="form-label" />
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button type="submit" variant="primary" loading={submitting}>
                    {submitting ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </div>
            </form>
          </div>

          <div className="detail-card">
            <h3 className="detail-card-title">How these apply</h3>
            <div className="t-body-sm" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p>
                <strong>Discount policy</strong>: TAKE_WHEN_ECONOMIC accepts 2 / 10 Net
                30-style discounts when the discount window is still open at the schedule
                date (almost always profitable at typical cost of capital). ALWAYS_TAKE
                forces them, ALWAYS_SKIP refuses them (and blocks pulling discount-eligible
                invoices earlier than their net due date).
              </p>
              <p>
                <strong>Dual-approval threshold</strong>: payment runs whose net total is
                ≥ this amount require two distinct approvers. The first ApprovePaymentRun
                call records the first approver; a second call by a different operator
                advances the run to APPROVED.
              </p>
              <p>
                <strong>Default payment method</strong>: used when a vendor's payment
                profile doesn't specify a method. Vendor's profile method overrides when
                set.
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
